import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'
import EmptyState from '~/components/empty-state'
import { ScorecardMutators } from '~/mutators/scorecards'
import { useTRPC } from '~/services/trpc'
import { SettingsContent } from '~/templates/settings'
import { SettingsActions, useSettingTitle } from '~/templates/settings'

import {
  Badge,
  Button,
  Icon,
  Search,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@hap/components'

export const Route = createFileRoute('/_authenticated/$alias/settings/scorecards/')({
  component: RouterComponent,
})

function RouterComponent() {
  const trpc = useTRPC()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const query = (search.query as string) || ''
  const filters = (search.filters as Record<string, unknown>) || {}

  const setQuery = (value: string | undefined) => {
    navigate({
      search: (search) => ({ ...search, query: value, page: 1 }),
      replace: true,
    })
  }

  const updateFilters = (filterUpdates: Record<string, unknown>) => {
    navigate({
      search: (search) => ({
        ...search,
        filters: { ...filters, ...filterUpdates },
        page: 1,
      }),
      replace: true,
    })
  }

  // Instant create pattern - like roles
  const { create, isPending } = ScorecardMutators.useCreate({
    onSuccess: (scorecardId) => {
      navigate({
        to: `/$alias/settings/scorecards/$scorecardId/criteria`,
        params: { scorecardId },
      })
    },
  })

  const workspaceFilter = filters.workspaceId as string | undefined

  // Fetch all scorecards for the organization
  // Note: getAll only returns id, name, archivedAt, and workspace info
  const { data: scorecards, isLoading } = useQuery(
    trpc.scorecards.getAll.queryOptions({
      workspaceIds: workspaceFilter ? [workspaceFilter] : undefined,
    }),
  )

  // Fetch workspaces for filtering
  const { data: workspaces } = useQuery(trpc.workspaces.getAll.queryOptions())

  // Filter by search query
  const filteredScorecards = useMemo(() => {
    if (!scorecards) return []
    let result = scorecards

    // Filter by search query
    if (query) {
      const q = query.toLowerCase()
      result = result.filter((scorecard) => scorecard.name.toLowerCase().includes(q))
    }

    return result
  }, [scorecards, query])

  useSettingTitle(['Scorecards'])

  return (
    <>
      <SettingsActions>
        <Button onClick={() => create()} loading={isPending} loadingLabel="Creating...">
          New Scorecard
        </Button>
      </SettingsActions>
      <SettingsContent spacing="sm" className="w-full overflow-y-auto">
        <header className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Search
              placeholder="Search by name"
              onSearch={(value) => setQuery(value || undefined)}
              className="flex w-125 max-w-md shrink-0 grow"
              defaultValue={query}
            />

            <Select
              value={workspaceFilter || 'all'}
              onValueChange={(value) =>
                updateFilters({ workspaceId: value === 'all' ? undefined : value })
              }
            >
              <SelectTrigger variant="ghost" size="sm-wd" className="w-50 shrink-0">
                <SelectValue placeholder="All workspaces" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All workspaces</SelectItem>
                {workspaces?.map((workspace) => (
                  <SelectItem key={workspace.id} value={workspace.id}>
                    {workspace.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </header>

        <div className="grid gap-3">
          {isLoading && (
            <div className="text-muted-foreground py-12 text-center">
              Loading scorecards...
            </div>
          )}
          {!isLoading && filteredScorecards.length === 0 && !query && (
            <EmptyState
              icon={<Icon name="list-checks" size={32} weight="fill" />}
              title="No scorecards yet"
              description="Scorecards help you evaluate customer interactions against defined criteria. Create your first scorecard to start measuring quality."
              actions={[
                {
                  label: 'Create scorecard',
                  onClick: create,
                },
              ]}
            />
          )}
          {!isLoading && filteredScorecards.length === 0 && query && (
            <div className="text-muted-foreground py-12 text-center">
              No scorecards match your search.
            </div>
          )}
          {filteredScorecards.map((scorecard) => {
            const isArchived = !!scorecard.archivedAt
            const isOrgWide = !scorecard.workspace

            return (
              <button
                onClick={() =>
                  navigate({
                    to: `/$alias/settings/scorecards/$scorecardId/criteria`,
                    params: { scorecardId: scorecard.id },
                  })
                }
                key={scorecard.id}
                className="hover:bg-accent flex items-center justify-between gap-4 rounded-md border p-4 text-left"
              >
                <div className="shrink grow space-y-1">
                  <p className="flex items-center gap-2 text-lg font-semibold">
                    {scorecard.name}
                    {isArchived && <Badge variant="outline">Archived</Badge>}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-4">
                  <Badge variant={isOrgWide ? 'secondary' : 'outline'}>
                    {isOrgWide ? 'Org-wide' : scorecard.workspace?.name}
                  </Badge>
                  <Icon name="caret-right" className="text-muted-foreground" />
                </div>
              </button>
            )
          })}
        </div>
      </SettingsContent>
    </>
  )
}
